import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const WINNERS = [
    { year: 2021, name: "Frank" },
    { year: 2022, name: "Timmy" },
    { year: 2023, name: "Rory" },
    { year: 2024, name: "Davey C" },
    { year: 2025, name: "Frank" },
];

/** Grass green, azalea pink, yellow. */
const LEAF_COLORS = ["#2d6a4a", "#e8487f", "#e8c547"];

function pseudoRandom(seed) {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
}

/** Full-viewport layer between dim backdrop and green dialog. */
function PastWinnersLeaves() {
    const leaves = useMemo(() => {
        const count = 150;
        return Array.from({ length: count }, (_, i) => {
            const r1 = pseudoRandom(i + 1);
            const r2 = pseudoRandom(i + 17);
            const r3 = pseudoRandom(i + 33);
            return {
                id: i,
                leftPct: r1 * 100,
                delaySec: r2 * 4,
                durationSec: 2.5 + r3 * 2.8,
                w: 5 + (i % 6) * 2,
                h: 8 + ((i + 3) % 7) * 2,
                color: LEAF_COLORS[i % LEAF_COLORS.length],
                startRotate: Math.round(pseudoRandom(i + 55) * 140),
                mirror: i % 2 === 1,
            };
        });
    }, []);

    return (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
            {leaves.map((leaf) => (
                <div
                    key={leaf.id}
                    className={`absolute opacity-90 motion-reduce:hidden ${
                        leaf.mirror
                            ? "motion-safe:animate-past-winners-leaf-fall-mirror"
                            : "motion-safe:animate-past-winners-leaf-fall"
                    }`}
                    style={{
                        left: `${leaf.leftPct}%`,
                        top: "-8%",
                        width: leaf.w,
                        height: leaf.h,
                        backgroundColor: leaf.color,
                        borderRadius:
                            "55% 45% 60% 40% / 55% 50% 50% 45%",
                        transform: `rotate(${leaf.startRotate}deg)`,
                        animationDuration: `${leaf.durationSec}s`,
                        animationDelay: `${leaf.delaySec}s`,
                    }}
                />
            ))}
        </div>
    );
}

export function PastWinnersModal({ open, onClose }) {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            e.preventDefault();
            e.stopPropagation();
            onClose();
        };
        window.addEventListener("keydown", onKey, { capture: true });
        return () =>
            window.removeEventListener("keydown", onKey, { capture: true });
    }, [open, onClose]);

    if (!mounted || !open) return null;

    const tree = (
        <div
            data-past-winners-overlay
            className="pointer-events-auto fixed inset-0 z-[48] flex items-end justify-center p-0 sm:items-center sm:p-4"
            role="presentation"
        >
            <button
                type="button"
                className="absolute inset-0 z-0 bg-black/50"
                aria-label="Close past winners"
                onClick={onClose}
            />
            <div className="pointer-events-none absolute inset-0 z-[1] overflow-hidden">
                <PastWinnersLeaves />
            </div>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="past-winners-title"
                onClick={(e) => e.stopPropagation()}
                className="relative z-10 mx-4 max-h-[min(88vh,560px)] w-full max-w-md overflow-hidden rounded-t-2xl border border-masters-gold/35 bg-masters-forest text-masters-gold shadow-2xl shadow-black/50 sm:mx-0 sm:rounded-2xl"
            >
                <div className="px-5 pb-6 pt-5">
                    <div className="flex items-start justify-between gap-3 border-b border-masters-gold/25 pb-3">
                        <h2
                            id="past-winners-title"
                            className="text-lg font-semibold text-masters-gold"
                        >
                            Past Winners
                        </h2>
                        <button
                            type="button"
                            onClick={onClose}
                            className="shrink-0 rounded-full p-2 text-masters-gold transition hover:bg-masters-gold/15 hover:text-masters-cream"
                            aria-label="Close"
                        >
                            <X className="h-5 w-5" strokeWidth={2} aria-hidden />
                        </button>
                    </div>
                    <ul className="mt-4 space-y-3 text-sm text-masters-gold sm:text-base">
                        {WINNERS.map((row) => (
                            <li
                                key={row.year}
                                className="grid grid-cols-[3.5rem_1fr] gap-x-3 tabular-nums"
                            >
                                <span className="font-semibold text-masters-gold">
                                    {row.year}
                                </span>
                                <span className="font-medium text-masters-gold">
                                    {row.name}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );

    return createPortal(tree, document.body);
}
